import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCommunicationMutations, useCommunications } from '../../hooks'
import type { Communication, CreateOutboundRequest } from '../../types/case-container'
import type { TFunction } from 'i18next'
import {
  CommunicationCard,
  EmptyState,
  ErrorState,
  Filters,
  Header,
  LoadingState,
} from './case-communications-tab/case-communications-tab.parts'
import { CreateCommunicationModal } from './case-communications-tab/create-communication-modal'

interface CaseCommunicationsTabProps {
  caseId: string
}

interface CommunicationsListProps {
  communications: Communication[]
  deleting: boolean
  onNew: () => void
  onDelete: (id: string) => void
  t: TFunction<'cases'>
}

function CommunicationsList({
  communications,
  deleting,
  onNew,
  onDelete,
  t,
}: CommunicationsListProps): ReactElement {
  if (communications.length === 0) {
    return <EmptyState onNew={onNew} t={t} />
  }
  return (
    <div className="space-y-3">
      {communications.map((comm) => (
        <CommunicationCard
          key={comm.id}
          comm={comm}
          deleting={deleting}
          onDelete={() => {
            onDelete(comm.id)
          }}
          t={t}
        />
      ))}
    </div>
  )
}

export function CaseCommunicationsTab({ caseId }: CaseCommunicationsTabProps): ReactElement {
  const { t } = useTranslation('cases')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [filterDirection, setFilterDirection] = useState<string | undefined>()
  const filterArg = filterDirection ? { direction: filterDirection } : {}
  const { communications, total, loading, error, refetch } = useCommunications(caseId, filterArg)
  const { creating, deleting, sendOutbound, remove } = useCommunicationMutations()

  const handleCreate = async (data: CreateOutboundRequest): Promise<void> => {
    const result = await sendOutbound(caseId, data)
    if (result) {
      setShowCreateModal(false)
      void refetch()
    }
  }

  const handleDelete = async (communicationId: string): Promise<void> => {
    if (!confirm(t('communications.confirmDelete'))) return
    const success = await remove(caseId, communicationId)
    if (success) void refetch()
  }

  if (loading) return <LoadingState t={t} />
  if (error)
    return (
      <ErrorState
        message={error}
        onRetry={() => {
          void refetch()
        }}
        t={t}
      />
    )

  return (
    <div className="space-y-6">
      <Header
        total={total}
        onRefresh={() => {
          void refetch()
        }}
        onNew={() => {
          setShowCreateModal(true)
        }}
        t={t}
      />
      <Filters filterDirection={filterDirection} onChange={setFilterDirection} t={t} />
      <CommunicationsList
        communications={communications}
        deleting={deleting}
        onNew={() => {
          setShowCreateModal(true)
        }}
        onDelete={(id) => {
          void handleDelete(id)
        }}
        t={t}
      />
      {showCreateModal && (
        <CreateCommunicationModal
          onClose={() => {
            setShowCreateModal(false)
          }}
          onCreate={(data) => {
            void handleCreate(data)
          }}
          creating={creating}
        />
      )}
    </div>
  )
}
